import React, { useMemo, useState } from "react";
import { useStepper } from "../ui/stepper";

import { CompanionFormProgress } from "../companionForm/CompanionFormProgress";
import { CompanionForm } from "../companionForm/CompanionForm";
import { CompanionFormStepItem } from "../companionForm/CompanionFormStepItem";
import { CompanionFormStep } from "../companionForm/CompanionFormStep";
import { CompanionFormProgressItem } from "../companionForm/CompanionFormProgressItem";
import { LandingFormIntroHeader } from "./LandingFormIntroHeader";
import {
  STYLES,
  GIRL_AGES,
  GIRL_ANIME_BODY_TYPE,
  GIRL_ANIME_EYE_COLOR,
  GIRL_ANIME_HAIR_COLORS,
  GIRL_ANIME_HAIR_LENGTH,
  GIRL_ANIME_HAIRSTYLES,
  GIRL_ANIME_RACE,
  GIRL_ANIME_SKIN_TONE,
  GIRL_ANIME_SUGGESTED_NAMES,
  GIRL_REALISM_BODY_TYPE,
  GIRL_REALISM_ETHNICITY,
  GIRL_REALISM_EYE_COLOR,
  GIRL_REALISM_HAIR_COLORS,
  GIRL_REALISM_HAIR_LENGTH,
  GIRL_REALISM_HAIRSTYLES,
  GIRL_REALISM_SUGGESTED_NAMES,

  // Landing Form Constants
  LP_FAKE_FORM_STEPS as STEPS,
  STEP_LABELS as STEP_LABELS,
  GIRL_STYLES,
  IGNORE_PROGRESS_STEPS,
} from "../../lib/constants";
import { LandingFormSummary } from "./LandingFormSummary";
import { LandingFormCreate } from "./LandingFormCreate";
import { LandingFormAuth } from "./LandingFormAuth";

export const LandingForm = () => {
  const { navigate: navigateStep, currentStep } = useStepper();

  const [data, setData] = useState({
    style: null,

    ethnicity: null,
    race: null,
    tone: null,

    body: null,
    eyes: null,

    hairColor: null,
    hairLength: null,
    hairStyle: null,

    soulName: null,
    soulAge: null,
    soulVoice: null,
  });

  const showProgress = useMemo(() => {
    return !IGNORE_PROGRESS_STEPS.includes(currentStep);
  }, [currentStep]);

  const getOptionsByStyle = (realismOptions, animeOptions) => {
    if (data.style === "realism") return realismOptions;
    if (data.style === "anime") return animeOptions;

    return [];
  };

  const handleChange = (key, value) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setData((object) => {
      Object.keys(object).forEach((key) => {
        object[key] = null;
      });

      return object;
    });
  };

  return (
    <CompanionForm headerHeight={currentStep === STEPS.INTRO ? 180 : 60}>
      {/* Header */}
      {currentStep === STEPS.INTRO && <LandingFormIntroHeader />}
      {/* Progress - Header items */}
      {showProgress && (
        <CompanionFormProgress>
          {Object.values(STEPS)
            .filter((step) => {
              if (IGNORE_PROGRESS_STEPS.includes(step)) return false;

              if (step === STEPS.HAIR_LENGTH || step === STEPS.HAIR_COLOR) return false;

              // hide the steps that are not relevant to the current style
              if (
                step !== STEPS.ETHNICITY &&
                step !== STEPS.RACE &&
                step !== STEPS.TONE
              )
                return true;

              if (data.style === STYLES.REALISM)
                return step !== STEPS.RACE && step !== STEPS.TONE;

              if (data.style === STYLES.ANIME) return step !== STEPS.ETHNICITY;

              return true;
            })
            .map((step, index) => {
              const isHairStep = step === STEPS.HAIR_STYLE;
              const isActiveHair = isHairStep && [STEPS.HAIR_STYLE, STEPS.HAIR_LENGTH, STEPS.HAIR_COLOR].includes(currentStep);

              return (
                <CompanionFormProgressItem
                  key={step}
                  pos={index + 1}
                  isActive={isActiveHair || currentStep === step}
                >
                  {STEP_LABELS[step]}
                </CompanionFormProgressItem>
              );
            })}
        </CompanionFormProgress>
      )}
      {/* Style */}
      <CompanionFormStep
        id={STEPS.INTRO}
        next={
          !!data.style &&
          (() =>
            navigateStep(() => {
              if (data.style === STYLES.REALISM) return STEPS.ETHNICITY;
              if (data.style === STYLES.ANIME) return STEPS.RACE;
            }))
        }
      >
        <div className="grid grid-cols-2 gap-4">
          {GIRL_STYLES.map((item) => (
            <CompanionFormStepItem
              key={item.id}
              value={item.id}
              label={item.label}
              image={item.image}
              hasSelected={!!data.style}
              isActive={data.style === item.id}
              onChange={(value) => handleChange("style", value)}
            />
          ))}
        </div>
      </CompanionFormStep>
      {/* Realism Ethnicity */}
      <CompanionFormStep
        id={STEPS.ETHNICITY}
        next={!!data.ethnicity && (() => navigateStep(STEPS.BODY))}
        back={() => {
          handleReset();
          navigateStep(STEPS.INTRO);
        }}
      >
        <h2 className="text-xl lg:text-2xl font-bold text-center">
          Choose Ethnicity
        </h2>

        <div className="grid grid-cols-2 gap-4 mt-6">
          {GIRL_REALISM_ETHNICITY.map((item) => (
            <CompanionFormStepItem
              key={item.id}
              value={item.id}
              label={item.label}
              image={item.image}
              hasSelected={!!data.ethnicity}
              isActive={data.ethnicity === item.id}
              onChange={(value) => handleChange("ethnicity", value)}
            />
          ))}
        </div>
      </CompanionFormStep>
      {/* Anime Race */}
      <CompanionFormStep
        id={STEPS.RACE}
        next={!!data.race && (() => navigateStep(STEPS.TONE))}
        back={() => {
          handleReset();
          navigateStep(STEPS.INTRO);
        }}
      >
        <h2 className="text-xl lg:text-2xl font-bold text-center">
          Choose Race
        </h2>

        <div className="grid grid-cols-2 gap-4 mt-6">
          {GIRL_ANIME_RACE.map((item) => (
            <CompanionFormStepItem
              key={item.id}
              value={item.id}
              label={item.label}
              image={item.image}
              hasSelected={!!data.race}
              isActive={data.race === item.id}
              onChange={(value) => handleChange("race", value)}
            />
          ))}
        </div>
      </CompanionFormStep>
      {/* Anime Tone */}
      <CompanionFormStep
        id={STEPS.TONE}
        next={!!data.tone && (() => navigateStep(STEPS.BODY))}
        back={() => navigateStep(STEPS.RACE)}
      >
        <h2 className="text-xl lg:text-2xl font-bold text-center">
          Choose Tone
        </h2>

        <div className="grid grid-cols-3 gap-4 mt-6">
          {GIRL_ANIME_SKIN_TONE.map((item) => (
            <CompanionFormStepItem
              key={item.id}
              value={item.id}
              label={item.label}
              image={item.image}
              hasSelected={!!data.tone}
              isActive={data.tone === item.id}
              onChange={(value) => handleChange("tone", value)}
            />
          ))}
        </div>
      </CompanionFormStep>
      {/* Body */}
      <CompanionFormStep
        id={STEPS.BODY}
        next={!!data.body && (() => navigateStep(STEPS.EYES))}
        back={() => {
          if (data.style === STYLES.REALISM) navigateStep(STEPS.ETHNICITY);
          if (data.style === STYLES.ANIME) navigateStep(STEPS.TONE);
        }}
      >
        <h2 className="text-xl lg:text-2xl font-bold text-center">
          Choose Body
        </h2>

        <div className="grid grid-cols-2 gap-4 mt-6">
          {getOptionsByStyle(GIRL_REALISM_BODY_TYPE, GIRL_ANIME_BODY_TYPE).map(
            (item) => (
              <CompanionFormStepItem
                key={item.id}
                value={item.id}
                label={item.label}
                image={item.image}
                hasSelected={!!data.body}
                isActive={data.body === item.id}
                onChange={(value) => handleChange("body", value)}
              />
            )
          )}
        </div>
      </CompanionFormStep>
      {/* Eyes */}
      <CompanionFormStep
        id={STEPS.EYES}
        next={!!data.eyes && (() => navigateStep(STEPS.HAIR_STYLE))}
        back={() => navigateStep(STEPS.BODY)}
      >
        <h2 className="text-xl lg:text-2xl font-bold text-center">
          Choose Eyes
        </h2>

        <div className="grid grid-cols-2 gap-4 mt-6">
          {getOptionsByStyle(GIRL_REALISM_EYE_COLOR, GIRL_ANIME_EYE_COLOR).map(
            (item) => (
              <CompanionFormStepItem
                key={item.id}
                value={item.id}
                label={item.label}
                image={item.image}
                hasSelected={!!data.eyes}
                isActive={data.eyes === item.id}
                onChange={(value) => handleChange("eyes", value)}
                imageClassName="w-full h-auto aspect-[16/6]"
              />
            )
          )}
        </div>
      </CompanionFormStep>
      {/* HAIR STYLE */}
      <CompanionFormStep
        id={STEPS.HAIR_STYLE}
        next={!!data.hairStyle && (() => navigateStep(STEPS.HAIR_LENGTH))}
        back={() => navigateStep(STEPS.EYES)}
      >
        <h2 className="text-xl lg:text-2xl font-bold text-center">
          Choose Hair Style
        </h2>

        <div className="grid grid-cols-3 gap-4 mt-4">
          {getOptionsByStyle(
            GIRL_REALISM_HAIRSTYLES,
            GIRL_ANIME_HAIRSTYLES
          ).map((item) => (
            <CompanionFormStepItem
              key={item.id}
              value={item.id}
              label={item.label}
              image={item.image}
              hasSelected={!!data.hairStyle}
              isActive={data.hairStyle === item.id}
              onChange={(value) => handleChange("hairStyle", value)}
            />
          ))}
        </div>
      </CompanionFormStep>
      {/* HAIR LENGTH */}
      <CompanionFormStep
        id={STEPS.HAIR_LENGTH}
        next={!!data.hairLength && (() => navigateStep(STEPS.HAIR_COLOR))}
        back={() => navigateStep(STEPS.HAIR_STYLE)}
      >
        <h2 className="text-xl lg:text-2xl font-bold text-center">
          Choose Hair Length
        </h2>

        <div className="grid grid-cols-3 gap-4 mt-4">
          {getOptionsByStyle(
            GIRL_REALISM_HAIR_LENGTH,
            GIRL_ANIME_HAIR_LENGTH
          ).map((item) => (
            <CompanionFormStepItem
              key={item.id}
              value={item.id}
              label={item.label}
              image={item.image}
              hasSelected={!!data.hairLength}
              isActive={data.hairLength === item.id}
              onChange={(value) => handleChange("hairLength", value)}
            />
          ))}
        </div>
      </CompanionFormStep>
      {/* HAIR COLOR */}
      <CompanionFormStep
        id={STEPS.HAIR_COLOR}
        next={!!data.hairColor && (() => navigateStep(STEPS.SOUL))}
        back={() => navigateStep(STEPS.HAIR_LENGTH)}
      >
        <h2 className="text-xl lg:text-2xl font-bold text-center">
          Choose Hair Color
        </h2>

        <div className="grid grid-cols-3 gap-4 mt-4">
          {getOptionsByStyle(
            GIRL_REALISM_HAIR_COLORS,
            GIRL_ANIME_HAIR_COLORS
          ).map((item) => (
            <CompanionFormStepItem
              key={item.id}
              value={item.id}
              label={item.label}
              image={item.image}
              hasSelected={!!data.hairColor}
              isActive={data.hairColor === item.id}
              onChange={(value) => handleChange("hairColor", value)}
            />
          ))}
        </div>
      </CompanionFormStep>
      {/* Soul */}
      <CompanionFormStep
        id={STEPS.SOUL}
        next={() => navigateStep(STEPS.SUMMARY)}
        back={() => navigateStep(STEPS.HAIR_COLOR)}
      >
        <h2 className="text-xl lg:text-2xl font-bold text-center">
          Choose Soul Name
        </h2>

        <div className="grid grid-cols-2 gap-4 mt-6">
          {getOptionsByStyle(
            GIRL_REALISM_SUGGESTED_NAMES,
            GIRL_ANIME_SUGGESTED_NAMES
          ).map((item) => (
            <CompanionFormStepItem
              key={item}
              value={item}
              label={item}
              hasSelected={!!data.soulName}
              isActive={data.soulName === item}
              onChange={(value) => handleChange("soulName", value)}
            />
          ))}
        </div>

        <h2 className="text-xl lg:text-2xl font-bold text-center mt-8">
          Choose Soul Age
        </h2>

        <div className="grid grid-cols-2 gap-4 mt-6">
          {GIRL_AGES.map((item) => (
            <CompanionFormStepItem
              key={item.id}
              value={item.id}
              label={item.label}
              hasSelected={!!data.soulAge}
              isActive={data.soulAge === item.id}
              onChange={(value) => handleChange("soulAge", value)}
            />
          ))}
        </div>
      </CompanionFormStep>
      {/* Summary */}
      <LandingFormSummary data={data} />
      {/* Create */}
      <LandingFormCreate data={data} />
      {/* Auth */}
      <LandingFormAuth data={data} />
    </CompanionForm>
  );
};
